import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// CSRF token management for tag service
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Create axios instance with HTTP-only cookie support
const API = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true, // Send HTTP-only cookies automatically
});

// Request interceptor to add CSRF token to state-changing requests
API.interceptors.request.use(
  async (config) => {
    // Only add CSRF token to state-changing methods
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (stateChangingMethods.includes(config.method.toUpperCase())) {
      // Fetch token if not already cached
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Add CSRF token to header
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle CSRF token expiration
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If we get a 403 CSRF error, try to refresh the token and retry
    if (error.response && error.response.status === 403 && error.response.data.error === 'CSRF token validation failed') {
      // Clear cached token and fetch a new one
      csrfToken = null;
      await fetchCsrfToken();
      
      // Retry the request with the new token
      const config = error.config;
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
        return API.request(config);
      }
    }
    return Promise.reject(error);
  }
);

// Get all tags
export const getTags = async () => {
  try {
    const response = await API.get('/admin/tags');
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to fetch tags';
  }
};

// Get single tag by ID
export const getTagById = async (id) => {
  try {
    const response = await API.get(`/admin/tags/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to fetch tag';
  }
};

// Create new tag
export const createTag = async (tagData) => {
  try {
    const response = await API.post('/admin/tags', tagData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to create tag';
  }
};

// Update tag
export const updateTag = async (id, tagData) => {
  try {
    const response = await API.put(`/admin/tags/${id}`, tagData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to update tag';
  }
};

// Delete tag
export const deleteTag = async (id) => {
  try {
    const response = await API.delete(`/admin/tags/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to delete tag';
  }
}; 